import React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { SubscriptionStatus } from '@/types/subscription';
import { formatQuota, formatQuotaBalance } from '@/utils/format-quota';

interface Props {
  brand: string;
  terms: { sessions: number; period: QUOTA_PERIOD };
  /** Como o valor do ciclo aparece; nulo esconde a linha de preço. */
  priceLabel?: string | null;
  status?: SubscriptionStatus | null;
  loading?: boolean;
}

/**
 * A assinatura na tela de agendamento — só os termos do plano.
 *
 * Não tem botão: assinar é o CTA do fim da tela, junto com os horários que o
 * cliente montou. Aqui ele lê o que está comprando; pedido, espera; ativo, vê
 * quantas sessões ainda tem antes de escolher o horário.
 */
export function SubscriptionPlanCard({
  brand,
  terms,
  priceLabel,
  status,
  loading,
}: Props) {
  const usage = status?.usage ?? null;
  const active = !!status?.active;
  const pending = !!status?.pending;

  return (
    <View
      className="gap-3 rounded-2xl p-4"
      style={{ backgroundColor: `${brand}0F`, borderCurve: 'continuous' }}
    >
      {/* Termos e valor na mesma linha: o plano é uma frase só. */}
      <View className="flex-row items-baseline gap-2">
        <ThemedText className="flex-1 text-[15px] font-semibold">
          {formatQuota(terms.sessions, terms.period)}
        </ThemedText>
        {!!priceLabel && (
          <ThemedText className="text-sm text-neutral-500">{priceLabel}</ThemedText>
        )}
      </View>

      {loading ? (
        <View className="items-center py-2">
          <ActivityIndicator color={brand} />
        </View>
      ) : active && usage ? (
        <ThemedText className="text-sm font-semibold" style={{ color: brand }}>
          {formatQuotaBalance(usage.remaining, usage.quota, usage.period)} · não
          acumula
        </ThemedText>
      ) : pending ? (
        <ThemedText className="text-sm text-neutral-600">
          Pedido enviado — o prestador precisa confirmar.
        </ThemedText>
      ) : (
        <ThemedText className="text-sm text-neutral-600">
          Escolha abaixo os horários que quer deixar marcados ao assinar.
        </ThemedText>
      )}
    </View>
  );
}
